import React from "react";
import Icon from "components/AppIcon";
import Image from "components/AppImage";


const POSITION_COLORS = {
  Shooter: "bg-purple-100 text-purple-700",
  Big: "bg-red-100 text-red-700",
  "Ball Handler": "bg-blue-100 text-blue-700",
  Cutter: "bg-green-100 text-green-700",
  "Plays D": "bg-amber-100 text-amber-700",
  Utility: "bg-gray-100 text-gray-700",
};

const PlayerTable = ({ players, sortConfig, onSort, onEdit, onDelete, onViewProfile }) => {
  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return "ChevronsUpDown";
    return sortConfig?.direction === "asc" ? "ChevronUp" : "ChevronDown";
  };

  const getInitials = (name) =>
    name?.split(" ")?.map((n) => n?.[0])?.join("")?.toUpperCase()?.slice(0, 2);

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: "var(--color-muted)", borderBottom: "1px solid var(--color-border)" }}>
              {[
                { label: "Player", key: "name" },
                { label: "Position", key: "position" },
                { label: "Games", key: "gamesPlayed" },
                { label: "Win %", key: "winPct" },
                { label: "PPG", key: "ppg" },
                { label: "RPG", key: "rpg" },
                { label: "APG", key: "apg" },
              ]?.map(({ label, key }) => (
                <th
                  key={key}
                  className="px-4 py-3 text-left font-caption font-semibold cursor-pointer select-none whitespace-nowrap"
                  style={{ color: "var(--color-muted-foreground)" }}
                  onClick={() => onSort(key)}
                >
                  <span className="flex items-center gap-1">
                    {label}
                    <Icon name={getSortIcon(key)} size={14} color="currentColor" />
                  </span>
                </th>
              ))}
              {(onEdit || onDelete) && (
              <th className="px-4 py-3 text-left font-caption font-semibold" style={{ color: "var(--color-muted-foreground)" }}>
                Actions
              </th>
              )}
            </tr>
          </thead>
          <tbody>
            {players?.map((player, idx) => (
              <tr
                key={player?.id}
                className="transition-athletic hover:bg-orange-50 dark:hover:bg-orange-900/10 cursor-pointer"
                style={{ borderBottom: "1px solid var(--color-border)", backgroundColor: idx % 2 === 0 ? "var(--color-card)" : "var(--color-background)" }}
                onClick={() => onViewProfile(player)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-heading font-bold text-sm text-white" style={{ backgroundColor: "var(--color-primary)" }}>
                      {player?.avatar ? (
                        <Image src={player?.avatar} alt={player?.avatarAlt} className="w-full h-full object-cover" />
                      ) : (
                        getInitials(player?.name)
                      )}
                    </div>
                    <div>
                      <p className="font-caption font-semibold text-sm" style={{ color: "var(--color-foreground)" }}>{player?.name}</p>
                      <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>#{player?.number}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold font-caption ${POSITION_COLORS?.[player?.position] || "bg-gray-100 text-gray-700"}`}>
                    {player?.position}
                  </span>
                </td>
                <td className="px-4 py-3 font-data text-sm" style={{ color: "var(--color-foreground)" }}>{player?.gamesPlayed}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-muted)" }}>
                      <div className="h-full rounded-full" style={{ width: `${player?.winPct}%`, backgroundColor: "var(--color-primary)" }} />
                    </div>
                    <span className="font-data text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>{player?.winPct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-data text-sm" style={{ color: "var(--color-foreground)" }}>{player?.ppg}</td>
                <td className="px-4 py-3 font-data text-sm" style={{ color: "var(--color-foreground)" }}>{player?.rpg}</td>
                <td className="px-4 py-3 font-data text-sm" style={{ color: "var(--color-foreground)" }}>{player?.apg}</td>
                {(onEdit || onDelete) && (
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {onEdit && (
                    <button
                      className="p-1.5 rounded transition-athletic hover:bg-blue-50"
                      onClick={() => onEdit(player)}
                      aria-label={`Edit ${player?.name}`}
                      title="Edit player"
                    >
                      <Icon name="Pencil" size={15} color="var(--color-secondary)" />
                    </button>
                    )}
                    {onDelete && (
                    <button
                      className="p-1.5 rounded transition-athletic hover:bg-red-50"
                      onClick={() => onDelete(player)}
                      aria-label={`Delete ${player?.name}`}
                      title="Delete player"
                    >
                      <Icon name="Trash2" size={15} color="var(--color-error)" />
                    </button>
                    )}
                  </div>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {players?.length === 0 && (
          <div className="py-16 text-center">
            <Icon name="Users" size={40} color="var(--color-muted-foreground)" />
            <p className="mt-3 font-caption text-sm" style={{ color: "var(--color-muted-foreground)" }}>No players found. Add your first player!</p>
          </div>
        )}
      </div>
      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-3 p-4">
        {players?.length === 0 && (
          <div className="py-12 text-center">
            <Icon name="Users" size={36} color="var(--color-muted-foreground)" />
            <p className="mt-2 font-caption text-sm" style={{ color: "var(--color-muted-foreground)" }}>No players found.</p>
          </div>
        )}
        {players?.map((player) => (
          <div
            key={player?.id}
            className="rounded-xl p-4 border transition-athletic hover-lift cursor-pointer"
            style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-sm)" }}
            onClick={() => onViewProfile(player)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-heading font-bold text-sm text-white" style={{ backgroundColor: "var(--color-primary)" }}>
                  {player?.avatar ? (
                    <Image src={player?.avatar} alt={player?.avatarAlt} className="w-full h-full object-cover" />
                  ) : (
                    getInitials(player?.name)
                  )}
                </div>
                <div>
                  <p className="font-caption font-semibold" style={{ color: "var(--color-foreground)" }}>{player?.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${POSITION_COLORS?.[player?.position] || "bg-gray-100 text-gray-700"}`}>{player?.position}</span>
                    <span className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>#{player?.number}</span>
                  </div>
                </div>
              </div>
              {(onEdit || onDelete) && (
              <div className="flex items-center gap-1" onClick={(e) => e?.stopPropagation()}>
                {onEdit && (
                <button className="p-2 rounded transition-athletic hover:bg-blue-50" onClick={() => onEdit(player)} aria-label={`Edit ${player?.name}`}>
                  <Icon name="Pencil" size={16} color="var(--color-secondary)" />
                </button>
                )}
                {onDelete && (
                <button className="p-2 rounded transition-athletic hover:bg-red-50" onClick={() => onDelete(player)} aria-label={`Delete ${player?.name}`}>
                  <Icon name="Trash2" size={16} color="var(--color-error)" />
                </button>
                )}
              </div>
              )}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3 pt-3" style={{ borderTop: "1px solid var(--color-border)" }}>
              {[
                { label: "Games", value: player?.gamesPlayed },
                { label: "Win%", value: `${player?.winPct}%` },
                { label: "PPG", value: player?.ppg },
                { label: "APG", value: player?.apg },
              ]?.map(({ label, value }) => (
                <div key={label} className="text-center">
                  <p className="font-data text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>{value}</p>
                  <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PlayerTable;